import { Device, DeviceId, DeviceInfo } from "@capacitor/device";
import {
  Firestore,
  collection,
  doc,
  serverTimestamp,
  setDoc,
  PartialWithFieldValue,
  DocumentReference,
  deleteField,
} from "firebase/firestore";
import { db as firestoreInstance } from "../firebase";
import { Device as DeviceRecord } from "@um/contracts";
import { BuildInfo, BundleInfo } from "@um/contracts";
import { Capacitor } from "@capacitor/core";
import { DeviceSettings } from "@um/contracts";
import { AccountRole } from "@um/contracts";

class DeviceInfoService {
  private static instance: DeviceInfoService;
  private firestore: Firestore;
  private userId: string | null = null;
  private deviceId: string | null = null;
  private deviceDocRef: DocumentReference<DeviceRecord> | null = null;

  private constructor() {
    this.firestore = firestoreInstance;
  }

  public static getInstance(): DeviceInfoService {
    if (!DeviceInfoService.instance) {
      DeviceInfoService.instance = new DeviceInfoService();
    }
    return DeviceInfoService.instance;
  }

  /**
   * SCENARIO 1: On sign-in - save all device info EXCEPT FCM token
   * Call this right after user authentication
   */
  public async updateDeviceInfo(
    userId: string,
    build: BuildInfo | null,
    bundle: BundleInfo | null
  ): Promise<void> {
    if (!userId) return;
    if (!Capacitor.isNativePlatform()) return;

    console.log(
      "[DeviceInfoService] Updating device info on sign-in for user:",
      userId
    );
    this.userId = userId;
    await this.ensureDeviceId();

    const deviceInfo = await Device.getInfo();
    const timezone = this.getTimezone();

    const data: PartialWithFieldValue<DeviceRecord> = {
      // Device information
      platform: deviceInfo.platform,
      osVersion: deviceInfo.osVersion,
      model: deviceInfo.model,
      manufacturer: deviceInfo.manufacturer,
      isVirtual: deviceInfo.isVirtual,
      name: deviceInfo.name,

      // Timezone (IANA identifier)
      timezone,

      // Native build info
      build,

      // OTA build info (if available at sign-in)
      bundle,

      // Update timestamp
      updatedAt: serverTimestamp(),
    } as any;

    // Save device info (without FCM token)
    await setDoc(this.getDeviceDocRef(), data, { merge: true });
    console.log(
      "[DeviceInfoService] Device info updated on sign-in. Timezone:",
      timezone
    );
  }

  /**
   * SCENARIO 2: Update FCM token only
   * Call this when FCM token is received or refreshed
   */
  public async updateFCMToken(token: string, userId?: string): Promise<void> {
    if (!token) return;

    console.log("[DeviceInfoService] Updating FCM token");

    // If userId is provided, ensure we're using it (re-initialize if needed)
    if (userId && userId !== this.userId) {
      console.log(
        "[DeviceInfoService] Updating userId from",
        this.userId,
        "to",
        userId
      );
      this.userId = userId;
      // Reset deviceDocRef so it gets recreated with the new userId
      this.deviceDocRef = null;
    }

    await this.ensureDeviceRefs();

    const data: PartialWithFieldValue<DeviceRecord> = {
      token,
      updatedAt: serverTimestamp(),
    };

    await setDoc(this.getDeviceDocRef(), data, { merge: true });
    console.log("[DeviceInfoService] FCM token updated");
  }

  /**
   * Update device settings (e.g., acknowledgedNotifications)
   */
  public async updateDeviceSettings(
    settings: Partial<DeviceSettings>,
    userId?: string
  ): Promise<void> {
    console.log("[DeviceInfoService] Updating device settings", settings);

    // If userId is provided, ensure we're using it (re-initialize if needed)
    if (userId && userId !== this.userId) {
      this.userId = userId;
      this.deviceDocRef = null;
    }

    await this.ensureDeviceRefs();

    const data: PartialWithFieldValue<DeviceRecord> = {
      settings: settings,
      updatedAt: serverTimestamp(),
    };

    await setDoc(this.getDeviceDocRef(), data, { merge: true });
    console.log("[DeviceInfoService] Device settings updated");
  }

  /**
   * Update device user selections (selectedMind and selectedRole)
   * Note: undefined values will delete the field from the document
   */
  public async updateUserSelection(
    selectedMind: DocumentReference<any> | null | undefined,
    selectedRole: AccountRole | undefined,
    userId?: string
  ): Promise<void> {
    console.log("[DeviceInfoService] Updating user selection", {
      selectedMind: selectedMind?.path,
      selectedRole,
    });

    if (userId && userId !== this.userId) {
      this.userId = userId;
      this.deviceDocRef = null;
    }

    await this.ensureDeviceRefs();

    const data: PartialWithFieldValue<DeviceRecord> = {
      updatedAt: serverTimestamp(),
    } as any;

    if (selectedMind) {
      data.selectedMind = selectedMind;
    } else if (selectedMind === null || selectedMind === undefined) {
      data.selectedMind = deleteField();
    }

    if (selectedRole !== undefined) {
      data.selectedRole = selectedRole;
    } else {
      data.selectedRole = deleteField();
    }

    await setDoc(this.getDeviceDocRef(), data, { merge: true });
    console.log("[DeviceInfoService] User selection updated");
  }

  /**
   * Initialize and return device reference (async version for subscriptions)
   */
  public async initializeDeviceRef(
    userId: string
  ): Promise<DocumentReference<DeviceRecord> | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    this.userId = userId;
    await this.ensureDeviceId();
    this.deviceDocRef = null; // Reset to force recreation

    return this.getDeviceDocRef();
  }

  /* ----------------- Private Helper Methods ----------------- */

  private async ensureDeviceRefs(): Promise<void> {
    if (!this.userId)
      throw new Error(
        "[DeviceInfoService] No user ID set - call updateDeviceInfoOnSignIn first"
      );
    if (!this.deviceId) await this.ensureDeviceId();
  }

  private async ensureDeviceId(): Promise<void> {
    if (this.deviceId) return;

    const deviceInfo: DeviceInfo = await Device.getInfo();
    const deviceIdObj: DeviceId = await Device.getId();

    this.deviceId =
      deviceIdObj.identifier ||
      `${deviceInfo.platform}-${deviceInfo.model}-${Date.now()}`;

    console.log("[DeviceInfoService] Device ID:", this.deviceId);
  }

  /**
   * Get the device timezone using the Intl API
   * Returns IANA timezone identifier (e.g., "America/New_York", "Asia/Kolkata")
   * This is the most reliable cross-platform method for Capacitor apps
   */
  private getTimezone(): string {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log("[DeviceInfoService] Detected timezone:", timezone);
      return timezone;
    } catch (error) {
      console.error("[DeviceInfoService] Failed to detect timezone:", error);
      return "";
    }
  }

  private getDeviceDocRef(): DocumentReference<DeviceRecord> {
    if (!this.userId || !this.deviceId) {
      throw new Error("[DeviceInfoService] Service not properly initialized");
    }

    if (!this.deviceDocRef) {
      this.deviceDocRef = doc(
        collection(this.firestore, `User/${this.userId}/Device`),
        this.deviceId
      ) as DocumentReference<DeviceRecord>;
    }

    return this.deviceDocRef;
  }
}

export const deviceInfoService = DeviceInfoService.getInstance();
